import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import type { User } from 'oidc-client-ts'
import { userManager } from './oidc'

type AuthContextValue = {
  user: User | null
  token: string | null
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Detect whether we're on the /callback page (Entra just redirected back with ?code=...).
    const isCallback = new URLSearchParams(window.location.search).has('code')

    // Load existing session from oidc-client-ts (checks sessionStorage for OIDC state)
    userManager.getUser().then((existingUser) => {
      if (existingUser && !existingUser.expired) {
        setUser(existingUser)
      }
      // On the callback page there's no token in sessionStorage yet — Callback.tsx
      // is about to call signinRedirectCallback() which will fire onUserLoaded.
      // Keeping isLoading=true here prevents a brief flash-redirect to "/" before
      // the new token arrives.
      if (!isCallback) {
        setIsLoading(false)
      }
    })

    const onUserLoaded = (u: User) => {
      setUser(u)
      setIsLoading(false)
    }
    const onUserUnloaded = () => setUser(null)

    userManager.events.addUserLoaded(onUserLoaded)
    userManager.events.addUserUnloaded(onUserUnloaded)

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded)
      userManager.events.removeUserUnloaded(onUserUnloaded)
    }
  }, [])

  const login = useCallback(async () => {
    await userManager.signinRedirect()
  }, [])

  const logout = useCallback(async () => {
    await userManager.signoutRedirect()
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        token: user?.access_token ?? null,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
